import { useState, type FormEvent } from 'react';
import { CompleteTask, TaskInput, TaskKind, type Task, type TaskStatus, type UpdateTask } from '@syndiqia/maintenance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, toNumber, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useMaintenanceMutation, useTasks, useVendors } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, centsOf, todayIso } from './MaintenanceNav';

const GROUPS: TaskStatus[] = ['overdue', 'due_soon', 'unscheduled', 'ok', 'inactive'];

export function CalendarPage() {
  const { t } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const tasks = useTasks();
  const manages = s.can('maintenance.manage');

  return (
    <MaintenanceFrame intro={m.calendarIntro} actions={manages && <Disclosure label={m.addTask}>{(close) => <TaskForm onDone={close} />}</Disclosure>}>
      {tasks.isPending && <Loading label={t.loading} />}
      {tasks.isError && <Alert>{errorMessage(tasks.error, t)}</Alert>}
      {manages && tasks.data && !tasks.data.some((x) => x.templateCode) && <AddStandard />}
      {tasks.data?.length === 0 && !manages && <Empty>{m.noTasks}</Empty>}
      {GROUPS.map((group) => {
        const items = tasks.data?.filter((x) => x.status === group) ?? [];
        if (items.length === 0) return null;
        return (
          <section key={group} className={`task-group task-group-${group}`}>
            <h2>
              {m.groups[group]} <span className="muted">({items.length})</span>
            </h2>
            <ul className="task-list">
              {items.map((task) => (
                <TaskRow key={task.id} task={task} manages={manages} />
              ))}
            </ul>
          </section>
        );
      })}
    </MaintenanceFrame>
  );
}

function AddStandard() {
  const { t } = useI18n();
  const m = t.maintenance;
  const add = useMaintenanceMutation<void, { added: number }>((api, base) => api(`${base}/tasks/standard`, { method: 'POST' }));
  return (
    <div className="panel callout">
      <p>{m.addStandardHint}</p>
      {add.data && <Alert tone="info">{m.standardAdded(add.data.added)}</Alert>}
      {add.isError && <Alert>{errorMessage(add.error, t)}</Alert>}
      <button type="button" className="button button-primary" disabled={add.isPending} onClick={() => add.mutate()}>
        {m.addStandard}
      </button>
    </div>
  );
}

function TaskRow({ task, manages }: { task: Task; manages: boolean }) {
  const { t, date } = useI18n();
  const m = t.maintenance;
  const [mode, setMode] = useState<'view' | 'done' | 'date'>('view');
  const update = useMaintenanceMutation<UpdateTask, Task>((api, base, input) => api(`${base}/tasks/${task.id}`, { method: 'PATCH', body: input }));

  return (
    <li className="task">
      <div className="task-main">
        <div>
          <strong>{task.title}</strong>
          <span className={`badge badge-kind-${task.kind}`}>{m.taskKinds[task.kind]}</span>
        </div>
        {task.description && <p className="muted small">{task.description}</p>}
        <p className="small task-facts">
          <span>{m.everyMonths(task.everyMonths)}</span>
          {task.lastDoneOn && (
            <span>
              {m.lastDone} : {date(task.lastDoneOn)}
            </span>
          )}
          {task.assetName && <span>{task.assetName}</span>}
          {task.reference && <span className="muted">{task.reference}</span>}
        </p>
      </div>
      <div className="task-due">
        {task.nextDueOn ? (
          <>
            <strong>{date(task.nextDueOn)}</strong>
            {task.daysLeft !== null && task.active && <span className={`task-days task-days-${task.status}`}>{m.daysLeft(task.daysLeft)}</span>}
          </>
        ) : (
          <span className="muted">{m.taskStatuses.unscheduled}</span>
        )}
        {manages && mode === 'view' && (
          <div className="task-actions">
            {task.active && (
              <button type="button" className="button button-small" onClick={() => setMode('done')}>
                ✓ {m.markDone}
              </button>
            )}
            <button type="button" className="link-button small" onClick={() => setMode('date')}>
              {m.setDate}
            </button>
            <button type="button" className="link-button small" disabled={update.isPending} onClick={() => update.mutate({ active: !task.active })}>
              {task.active ? m.deactivate : m.reactivate}
            </button>
          </div>
        )}
      </div>
      {mode === 'done' && <DoneForm task={task} onDone={() => setMode('view')} />}
      {mode === 'date' && <DateForm task={task} onDone={() => setMode('view')} />}
      {update.isError && <Alert>{errorMessage(update.error, t)}</Alert>}
    </li>
  );
}

function DoneForm({ task, onDone }: { task: Task; onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const vendors = useVendors();
  const [values, setValues] = useState({ doneOn: todayIso(), note: '', cost: '', vendorId: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const complete = useMaintenanceMutation<CompleteTask, Task>((api, base, input) => api(`${base}/tasks/${task.id}/complete`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { cost, ...rest } = values;
    const result = validate(CompleteTask, { ...rest, costCents: centsOf(cost) });
    setErrors(result.errors ?? {});
    if (result.data) complete.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="task-form form" noValidate>
      <div className="row">
        <Field label={m.doneOn} error={errors.doneOn}>
          <input type="date" value={values.doneOn} max={todayIso()} onChange={set('doneOn')} />
        </Field>
        <Field label={m.vendor} hint={t.common.optional}>
          <select value={values.vendorId} onChange={set('vendorId')}>
            <option value="">{m.none}</option>
            {vendors.data?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.cost} hint={t.common.optional} error={errors.costCents}>
          <input inputMode="decimal" value={values.cost} onChange={set('cost')} placeholder="0,00 $" />
        </Field>
      </div>
      <Field label={m.doneNote} hint={t.common.optional}>
        <input value={values.note} onChange={set('note')} />
      </Field>
      <p className="muted small">{m.completeHint}</p>
      {complete.isError && <Alert>{errorMessage(complete.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={complete.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

function DateForm({ task, onDone }: { task: Task; onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const [nextDueOn, setNextDueOn] = useState(task.nextDueOn ?? '');
  const [months, setMonths] = useState(task.everyMonths?.toString() ?? '');
  const update = useMaintenanceMutation<UpdateTask, Task>((api, base, input) => api(`${base}/tasks/${task.id}`, { method: 'PATCH', body: input }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({ nextDueOn: nextDueOn || null, everyMonths: toNumber(months) ?? null }, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="task-form form" noValidate>
      <div className="row">
        <Field label={m.nextDue}>
          <input type="date" value={nextDueOn} onChange={(e) => setNextDueOn(e.target.value)} autoFocus />
        </Field>
        <Field label={m.monthsLabel} hint={t.common.optional}>
          <input inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} />
        </Field>
      </div>
      {update.isError && <Alert>{errorMessage(update.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={update.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

function TaskForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const [values, setValues] = useState({ kind: 'preventive', title: '', description: '', everyMonths: '12', nextDueOn: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useMaintenanceMutation<TaskInput, Task>((api, base, input) => api(`${base}/tasks`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const result = validate(TaskInput, { ...values, everyMonths: toNumber(values.everyMonths) });
    setErrors(result.errors ?? {});
    if (result.data) create.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={m.taskTitle} error={errors.title}>
          <input value={values.title} onChange={set('title')} autoFocus />
        </Field>
        <Field label={m.kind}>
          <select value={values.kind} onChange={set('kind')}>
            {TaskKind.options.map((k) => (
              <option key={k} value={k}>
                {m.taskKinds[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={m.description} hint={t.common.optional}>
        <input value={values.description} onChange={set('description')} />
      </Field>
      <div className="row">
        <Field label={m.monthsLabel} hint={t.common.optional} error={errors.everyMonths}>
          <input inputMode="numeric" value={values.everyMonths} onChange={set('everyMonths')} />
        </Field>
        <Field label={m.nextDue} hint={t.common.optional} error={errors.nextDueOn}>
          <input type="date" value={values.nextDueOn} onChange={set('nextDueOn')} />
        </Field>
      </div>
      {create.isError && <Alert>{errorMessage(create.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={create.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
